import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useMediaPredicate } from 'react-media-hook';

import { openModal } from 'reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import { fetchPortfolio, getIsPropertyInPortfolio } from 'reducers/portfolioReducer';
import { fetchPortfolioSettingsByPropertyId } from 'reducers/portfolioSettingsReducer';
import { removePropertyFromPortfolioSet } from 'data/reducers/portfoliosManagementReducer';
import { showToast } from 'data/reducers/toastReducer';
import { areFirstLoadTasksFinished } from 'utils/firstLoadTasks';

import OutboundLink from 'components/OutboundLink';
import Spinner from 'components/Spinner';
import Tooltip from 'components/Tooltip';
import { MOBILE_SIZE } from 'utils/mediaQueryUtils';

import './ToolbarIcons.css';

const ToolbarIcons = () => {
	const isMobile = useMediaPredicate(MOBILE_SIZE);
	const { address } = useParams();
	const dispatch = useDispatch();
	const {
		propertyData,
		firstLoadTasks,
		userId,
		isLoggedIn,
		propertyInPortfolio,
	} = useSelector((state) => ({
		propertyData: state.property.propertyData,
		firstLoadTasks: state.firstLoadTasks.data,
		userId: state.user.userData?.id,
		isLoggedIn: !!state.user.userData?.id,
		propertyInPortfolio: state.portfolio.propertyInPortfolio,
	}));
	let showRoadview = !!propertyData?.lat && !!propertyData?.long;
	let roadviewUrl = `https://roadview.planninglabs.nyc/view/${propertyData?.long}/${propertyData?.lat}`;
	let ecbUrl = 'https://ecb.vio.nyc';
	// An errored task is finished too (e.g. DOT Liens pending the operator sweep).
	const areFirstLoadTasksComplete = useMemo(
		() => areFirstLoadTasksFinished(firstLoadTasks),
		[firstLoadTasks],
	);

	const handleTrash = () => {
		if (!isLoggedIn || !propertyInPortfolio?.id || !propertyInPortfolio?.portfolio_set?.id) {
			return;
		}

		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				color: 'danger',
				title: 'Remove Property',
				confirmLabel: 'Remove Property',
				children: (
					<div>
						Are you sure you want to remove &quot;
						{propertyData?.address || 'this property'}&quot; from your portfolio?
					</div>
				),
				onConfirm: async () => {
					try {
						await dispatch(
							removePropertyFromPortfolioSet(
								propertyInPortfolio.portfolio_set.id,
								propertyInPortfolio.id,
							),
						);
						// Refresh portfolio membership so the toolbar and star drop the removed property
						dispatch(fetchPortfolio(userId));
						dispatch(fetchPortfolioSettingsByPropertyId(propertyData?.id));
						dispatch(getIsPropertyInPortfolio(propertyData?.id));
						dispatch(
							showToast({
								title: 'Success!',
								body: 'Property removed from portfolio.',
								type: 'success',
							}),
						);
					} catch (error) {
						dispatch(
							showToast({
								title: 'Error!',
								body: 'Failed to remove property from portfolio.',
								type: 'error',
							}),
						);
					}
				},
			}),
		);
	};

	const toolbarButtons = (
		<div className="toolbar-buttons d-flex align-items-center">
			{showRoadview && (
				<span className="toolbar-icon-wrapper">
					<OutboundLink url={roadviewUrl} noIcon>
						<Tooltip text="Road View" direction="bottom">
							<i className="bi bi-eye" />
						</Tooltip>
					</OutboundLink>
				</span>
			)}
			<span className="toolbar-icon-wrapper">
				<button
					className="btn-hyperlink toolbar-button"
					onClick={() =>
						dispatch(
							openModal(MODAL_TYPES.PROPERTY_NOTIFICATION_MODAL, {
								property: propertyData,
							}),
						)
					}
				>
					<Tooltip text="Email Alerts" direction="bottom">
						<i className="bi bi-bell text-danger-icon" />
					</Tooltip>
				</button>
			</span>

			<span className="toolbar-icon-wrapper">
				<Link to={`/property/${address}/tasks`}>
					<Tooltip text="Property Data Progress" direction="bottom">
						<span
							className={`fancy-hourglass ${
								areFirstLoadTasksComplete ? 'complete' : 'in-progress'
							}`}
						>
							<i className="bi bi-hourglass-split" />
							{areFirstLoadTasksComplete ? (
								<i className="bi bi-check-circle-fill check" />
							) : (
								<Spinner size="sm" color="primary" />
							)}
						</span>
					</Tooltip>
				</Link>
			</span>

			<span className="toolbar-icon-wrapper">
				<OutboundLink url={ecbUrl} noIcon>
					<Tooltip text="ECB" direction="bottom">
						<i className="bi bi-bank" />
					</Tooltip>
				</OutboundLink>
			</span>

			<span className="toolbar-icon-wrapper">
				<Link to="/backend-search">
					<Tooltip text="Backend Search" direction="bottom">
						<i className="bi bi-door-open-fill" />
					</Tooltip>
				</Link>
			</span>

			{!isMobile && (
				<span className="toolbar-icon-wrapper">
					<button
						className="btn-hyperlink toolbar-button"
						onClick={() => dispatch(openModal(MODAL_TYPES.EXPORT_REPORT))}
					>
						<Tooltip text="Export Report" direction="bottom">
							<i className="bi bi-cloud-download-fill" />
						</Tooltip>
					</button>
				</span>
			)}

			{isLoggedIn && propertyInPortfolio?.id && (
				<span className="toolbar-icon-wrapper">
					<button
						className="btn-hyperlink toolbar-button"
						onClick={handleTrash}
					>
						<Tooltip text="Remove Property" direction="bottom">
							<i className="bi bi-trash" />
						</Tooltip>
					</button>
				</span>
			)}
		</div>
	);

	return <div className="rd-toolbar-icons">{toolbarButtons}</div>;
};

export default ToolbarIcons;
